import { useEffect } from 'react';
import {
  ButtonColor,
  ButtonVariant,
  Icon,
  TextArea,
  TextButton,
  TextInput,
  TextInputType,
} from '../../../../components';
import { MIN_REQUIRED_SCENARIO_ITEMS } from '../../../../constants';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import {
  addQuestion,
  deleteQuestion,
  setIsAnsweredQuestions,
  setIsDynamicQuestions,
  setNumOfDynamicQuestions,
  updateQuestion,
} from '../../../../redux/reducers';
import { ComponentSize } from '../../../../types';
import { isValueFilled } from '../../../../utils';
import ProspectCheckbox from '../ProspectCheckbox';
import TabSection from '../TabSection';
import DynamicItemsSelector from './DynamicItemsSelector';
import ScenarioListItem, { MAX_SCENARIO_ITEM_ROWS, MIN_SCENARIO_ITEM_ROWS } from './ScenarioListItem';

const SELECT_POINTS_WIDTH = 80;

const Questions = () => {
  const dispatch = useAppDispatch();
  const {
    fields: { questions, isAnsweredQuestions, isDynamicQuestions: isDynamicQuestionsChecked, numOfDynamicQuestions },
  } = useAppSelector((state) => state.prospect);

  const canDelete = questions.length > MIN_REQUIRED_SCENARIO_ITEMS;
  const numFilledQuestions = questions.filter((q) => !!q.value.trim()).length;

  // Delete empty questions on unmount.
  useEffect(() => {
    return () => {
      if (questions.length > MIN_REQUIRED_SCENARIO_ITEMS) {
        questions.forEach((question, index) => {
          if (!question.value) {
            dispatch(deleteQuestion(index));
          }
        });
      }
    };
  }, []);

  return (
    <TabSection title="Questions">
      {questions.map((question, index) => (
        <ScenarioListItem
          key={index}
          index={index}
          // Use a unique key for the text area to force a re-render when the show answer checkbox changes
          // This is to update the text area rows attribute
          fieldKey={`question-${index}-${isAnsweredQuestions ? 'with-answers' : 'without-answers'}`}
          canDelete={canDelete}
          content={
            isAnsweredQuestions && (
              <>
                <TextArea
                  fitContent
                  maxRows={MAX_SCENARIO_ITEM_ROWS}
                  rows={MAX_SCENARIO_ITEM_ROWS}
                  placeholder="Answer"
                  value={question.answer}
                  onChange={(e) => dispatch(updateQuestion({ index, answer: e.target.value }))}
                />
                <TextInput
                  className="flex-shrink-0"
                  width={SELECT_POINTS_WIDTH}
                  type={TextInputType.NUMBER}
                  onChange={(e) => dispatch(updateQuestion({ index, points: parseInt(e.target.value) }))}
                  value={question.points !== undefined && !isNaN(question.points) ? question.points.toString() : ''}
                  showNumberControls
                  minValue={0}
                  size={ComponentSize.MEDIUM} // Size is medium to match text area padding.
                />
              </>
            )
          }
          // If add answers is checked and the question has a value, it is required to fill its answer and points
          isRequired={index < MIN_REQUIRED_SCENARIO_ITEMS || (isAnsweredQuestions && isValueFilled(question.value))}
          minRows={isAnsweredQuestions ? MAX_SCENARIO_ITEM_ROWS : MIN_SCENARIO_ITEM_ROWS}
          value={question.value}
          placeholder={index === 0 ? 'How does your pricing structure work?' : undefined}
          deleteItem={() => dispatch(deleteQuestion(index))}
          updateItem={(value) => dispatch(updateQuestion({ index, value }))}
        />
      ))}
      <div className="flex items-center gap-4">
        <TextButton
          text="Add question"
          startIcon={Icon.PLUS}
          color={ButtonColor.NEUTRAL}
          variant={ButtonVariant.SOFT}
          onClick={() => {
            dispatch(addQuestion());
          }}
        />
        <ProspectCheckbox
          checked={isAnsweredQuestions}
          label="Add answers"
          setChecked={(checked) => {
            dispatch(setIsAnsweredQuestions(checked));
          }}
        />
        <DynamicItemsSelector
          isDynamicChecked={isDynamicQuestionsChecked}
          label="question"
          numOfDynamicItems={numOfDynamicQuestions}
          totalItems={numFilledQuestions}
          setIsDynamicChecked={(newChecked) => dispatch(setIsDynamicQuestions(newChecked))}
          setNumOfDynamicItems={(newNumOfDynamicItems) => dispatch(setNumOfDynamicQuestions(newNumOfDynamicItems))}
        />
      </div>
    </TabSection>
  );
};

export default Questions;
